import { useId, useState } from 'react'
import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'
import {
  BookOpen,
  Clock3,
  Image as ImageIcon,
  Mic2,
  Pencil,
  RefreshCw,
  UserRound,
} from 'lucide-react'
import clsx from 'clsx'
import { LearningContentText } from '@quebon/shared-learning/components/common/LearningContentText'
import { getDateLocale } from '@/utils/dateLocale'
import { LcEvidenceVideoPlayer } from '@/components/common/LcEvidenceVideoPlayer'
import { GrowthAlertAudioPlayer } from './GrowthAlertAudioPlayer'
import type {
  GrowthAlertComponentPhotosView,
  GrowthAlertEssentialCheckView,
  GrowthAlertLessonView,
  GrowthAlertPreviewResponse,
  GrowthAlertReviewNoteSummary,
  GrowthAlertSessionView,
  UnderstandingSignalKey,
  GrowthAlertVideoView,
} from '@/types/coaching'

export type GrowthAlertSurface = 'public' | 'dialog'

type TFunc = ReturnType<typeof useTranslation>['t']

const REVIEW_NOTE_SOURCE_ORDER: Record<string, number> = { WRONG: 0, ESSENTIAL: 1, ALL: 2 }
const COMPONENT_PHOTO_NAME_KEYS = new Map([
  ['video', 'video'], ['quiz', 'quiz'], ['guide', 'guide'], ['toon', 'toon'],
  ['assignment', 'assignment'], ['interactive', 'interactive'], ['buildup', 'buildup'], ['warmup', 'warmup'],
  ['reviewNote', 'reviewNote'], ['workbook', 'workbook'], ['essentialCheck', 'essentialCheck'],
])
// Shorten only known default labels; retain every other configured component name.
const COMPONENT_PHOTO_LABEL_ALIASES = new Map<string, ReadonlyMap<string, string>>([
  ['buildup', new Map([['빌드업 1~4단계 (+ 서술형 답 말해보기)', 'growthAlert.photoComponentNames.buildup']])],
  ['warmup', new Map([['생각열기 & 포인트 · 포인트 말해보기', 'growthAlert.photoComponentNames.warmup']])],
  ['guide', new Map([['설명하기 · 서술형 답 말해보기', 'growthAlert.explain']])],
  ['quiz', new Map([
    ['문제 풀기 (보기 표시 X)', 'growthAlert.photoComponentNames.quiz'],
    ['실전 연습 문제 풀기 (보기 표시 X)', 'growthAlert.photoComponentNames.practice'],
    ['복습 문제 풀기', 'growthAlert.photoComponentNames.reviewQuiz'],
  ])],
  ['interactive', new Map([
    ['인터렉티브 활동', 'growthAlert.photoComponentNames.interactiveActivity'],
    ['인터렉티브 활동(깨보드)', 'growthAlert.photoComponentNames.queboard'],
  ])],
])

interface Props {
  preview: GrowthAlertPreviewResponse | null | undefined
  isLoading: boolean
  isError: boolean
  t: TFunc
  onOpenPhoto: (url: string) => void
  surface?: GrowthAlertSurface
  onRefreshVideoUrl?: (id: string) => Promise<string | undefined>
}

export function GrowthAlertContent({
  preview,
  isLoading,
  isError,
  t,
  onOpenPhoto,
  surface = 'dialog',
  onRefreshVideoUrl,
}: Props) {
  if (isLoading) {
    return <ReportStatus tone="muted" message={t('growthAlert.loading')} />
  }

  if (isError) {
    return <ReportStatus tone="error" message={t('growthAlert.error')} />
  }

  if (!preview || !preview.available) {
    const reason = preview?.unavailableReason ?? 'NO_SESSION'
    return <ReportStatus tone="muted" message={t(`growthAlert.unavailable.${reason}`)} />
  }

  return (
    <div className="bg-[#f7f7f7] text-[#131313]">
      <GrowthAlertSummary preview={preview} t={t} surface={surface} />

      <div>
        {preview.lessons.map((lesson, index) => (
          <LessonSection
            key={`${lesson.lessonId}-${lesson.lessonKind}-${index}`}
            lesson={lesson}
            index={index}
            legacy={(preview.formatVersion ?? 1) < 2}
            t={t}
            onOpenPhoto={onOpenPhoto}
            surface={surface}
            onRefreshVideoUrl={onRefreshVideoUrl}
          />
        ))}
      </div>

      {preview.classSession?.teacherComment ? (
        <section
          className={clsx(
            'px-5 pt-5 md:px-8 md:pt-8',
            surface === 'public' ? 'pb-0' : 'pb-8',
          )}
        >
          <div className="rounded-2xl border border-[#4599ff] bg-[#edf5ff] p-4 shadow-[0_6px_8px_rgba(44,62,53,0.04)] md:p-6">
            <div className="flex items-center gap-2">
              <span className="flex size-6 items-center justify-center rounded-lg bg-[#4599ff] text-white">
                <UserRound className="size-4" aria-hidden="true" />
              </span>
              <h2 className="text-lg font-bold md:text-xl">
                {t('growthAlert.teacherComment')}
              </h2>
            </div>
            <p className="mt-6 whitespace-pre-wrap text-sm font-medium leading-[1.6] md:text-[15px] md:leading-[1.7]">
              {preview.classSession.teacherComment}
            </p>
          </div>
        </section>
      ) : null}
    </div>
  )
}

function GrowthAlertSummary({
  preview,
  t,
  surface,
}: {
  preview: GrowthAlertPreviewResponse
  t: TFunc
  surface: GrowthAlertSurface
}) {
  const learnerName = preview.learner?.fullName
  const session = preview.classSession

  return (
    <section
      className={clsx(
        'border-b border-black/10 bg-white shadow-[0_4px_6px_rgba(44,62,53,0.04)]',
        surface === 'public'
          ? 'px-5 pb-6 pt-2 md:rounded-2xl md:px-8 md:py-6'
          : 'px-5 py-6 md:px-8',
      )}
    >
      <div
        className={clsx(
          'flex flex-col gap-5',
          surface === 'public' && 'lg:flex-row lg:items-start lg:gap-6',
        )}
      >
        <div className="min-w-0 flex-1 px-2 md:px-0">
          {learnerName ? (
            <h1 className="text-[22px] font-extrabold md:text-[26px]">
              {t('growthAlert.learnerName', { name: learnerName })}
            </h1>
          ) : null}
          {session ? (
            <p className="mt-2 text-sm font-medium leading-[21px] text-[#888] md:text-[15px] md:leading-[22px]">
              {formatSessionLine(session)}
            </p>
          ) : null}
        </div>

        <div className={clsx('flex gap-3 md:gap-6', surface === 'public' && 'lg:flex-1')}>
          <SummaryStat
            icon={<Clock3 className="size-5" aria-hidden="true" />}
            label={t('growthAlert.learningTime')}
            value={session?.durationMinutes != null
              ? t('growthAlert.minutesValue', { minutes: session.durationMinutes })
              : '-'}
            tone="blue"
          />
          <SummaryStat
            icon={<BookOpen className="size-5" aria-hidden="true" />}
            label={t('growthAlert.lessonCount')}
            value={t('growthAlert.lessonCountValue', { count: preview.lessons.length })}
            tone="mint"
          />
        </div>
      </div>
    </section>
  )
}

function SummaryStat({
  icon,
  label,
  value,
  tone,
}: {
  icon: React.ReactNode
  label: string
  value: string
  tone: 'blue' | 'mint'
}) {
  const isBlue = tone === 'blue'
  return (
    <div
      className={clsx(
        'flex min-w-0 flex-1 items-center justify-between rounded-xl px-3.5 py-3 md:rounded-2xl md:px-[18px] md:py-4',
        isBlue ? 'bg-[#e8f2ff]' : 'bg-[#e6f7f7]',
      )}
    >
      <span
        className={clsx(
          'flex min-w-0 items-center gap-1 text-sm font-medium md:gap-2 md:text-[15px] md:font-semibold',
          isBlue ? 'text-[#4599ff]' : 'text-[#29b4b5]',
        )}
      >
        {icon}
        <span className="truncate">{label}</span>
      </span>
      <span className="ml-2 shrink-0 text-base font-semibold md:text-lg md:font-bold">{value}</span>
    </div>
  )
}

function LessonSection({
  lesson,
  index,
  legacy,
  t,
  onOpenPhoto,
  surface,
  onRefreshVideoUrl,
}: {
  lesson: GrowthAlertLessonView
  index: number
  legacy: boolean
  t: TFunc
  onOpenPhoto: (url: string) => void
  surface: GrowthAlertSurface
  onRefreshVideoUrl?: (id: string) => Promise<string | undefined>
}) {
  const chapterTitle = lesson.chapterTitle?.trim() || lesson.chapterLabel?.trim()
  const fallbackSubtitleParts = dedupeSubtitleParts([
    lesson.chapterLabel,
    lesson.chapterTitle,
    lesson.lessonLabel,
  ])
  const displayLessonTitle = lesson.lessonNumber != null
    ? t('growthAlert.numberedLessonTitle', {
        number: lesson.lessonNumber,
        title: lesson.lessonTitle,
      })
    : lesson.lessonTitle
  const displayChapterTitle = lesson.chapterNumber != null && chapterTitle
    ? t('growthAlert.numberedChapterTitle', {
        number: lesson.chapterNumber,
        title: chapterTitle,
      })
    : fallbackSubtitleParts.join(' · ')
  const isReview = lesson.lessonKind === 'designated_review'

  return (
    <section
      data-testid="growth-alert-lesson-section"
      aria-label={t('growthAlert.lessonSection', { index: index + 1 })}
      className={clsx(
        'border-b-4 border-[#e5e5e5] last:border-b-0',
        surface === 'public' && 'lg:[content-visibility:auto]',
      )}
    >
      <header className="px-7 pb-5 pt-6 md:p-8">
        <div className="flex min-h-5 items-center gap-2 md:gap-4">
          <p className="text-sm font-bold text-[#4599ff] md:text-base md:font-extrabold">
            {t('growthAlert.lessonSection', { index: index + 1 })}
          </p>
          {lesson.curriculumTitle ? (
            <p className="min-w-0 flex-1 truncate text-xs font-medium text-[#888] md:text-base">
              {lesson.curriculumTitle}
            </p>
          ) : <span className="flex-1" />}
          <span
            className={clsx(
              'rounded-full px-3 py-1 text-xs font-bold md:px-3.5 md:py-1.5 md:text-sm md:font-extrabold',
              isReview
                ? 'bg-[#ffaa0026] text-[#ffaa00]'
                : 'bg-[#4599ff26] text-[#4599ff]',
            )}
          >
            {t(isReview ? 'growthAlert.reviewBadge' : 'growthAlert.progressBadge')}
          </span>
        </div>
        <h2 className="mt-1 text-xl font-extrabold md:mt-2 md:text-2xl"><ContentMetadataText text={displayLessonTitle} /></h2>
        {displayChapterTitle ? (
          <p className="mt-1 flex min-h-8 items-center text-sm font-medium text-[#888] md:text-[15px]">
            <ContentMetadataText text={displayChapterTitle} />
          </p>
        ) : null}
      </header>

      <div className="space-y-4 px-5 pb-6 md:px-8 md:pb-8">
        {legacy ? (
          <LegacyLessonContent
            lesson={lesson}
            t={t}
            onOpenPhoto={onOpenPhoto}
            onRefreshVideoUrl={onRefreshVideoUrl}
          />
        ) : (
          <CurrentLessonContent
            lesson={lesson}
            t={t}
            onOpenPhoto={onOpenPhoto}
            surface={surface}
            onRefreshVideoUrl={onRefreshVideoUrl}
          />
        )}
      </div>
    </section>
  )
}

function CurrentLessonContent({
  lesson,
  t,
  onOpenPhoto,
  surface,
  onRefreshVideoUrl,
}: {
  lesson: GrowthAlertLessonView
  t: TFunc
  onOpenPhoto: (url: string) => void
  surface: GrowthAlertSurface
  onRefreshVideoUrl?: (id: string) => Promise<string | undefined>
}) {
  const reviewNoteSummaries = orderedReviewNoteSummaries(lesson)
  const explainRecordings = [
    { key: 'first', url: lesson.explain.firstAudioUrl?.trim(), label: t('growthAlert.firstRecording') },
    { key: 'second', url: lesson.explain.audioUrl?.trim(), label: t('growthAlert.secondRecording') },
  ]
  const hasThinking = Boolean(lesson.thinkingOpenerAudioUrl || lesson.thinkingPoint)
  const hasExplain = explainRecordings.some(({ url }) => Boolean(url)) || lesson.explain.photoUrls.length > 0 || (lesson.explain.videos?.length ?? 0) > 0
  const hasOutcomes = Boolean(
    lesson.lessonQuizSummary || reviewNoteSummaries.length > 0 || lesson.workbookSummary,
  )
  const hasChecks = lesson.essentialChecks.length > 0
  let nextStep = 1
  const thinkingStep = hasThinking ? nextStep++ : null
  const explainStep = hasExplain ? nextStep++ : null
  const outcomesStep = hasOutcomes ? nextStep++ : null
  const checksStep = hasChecks ? nextStep++ : null

  return (
    <>
      {thinkingStep ? (
        <StepCard
          step={thinkingStep}
          title={t('growthAlert.thinkingOpener')}
          description={t('growthAlert.thinkingDescription')}
          surface={surface}
        >
          {lesson.thinkingOpenerAudioUrl ? (
            <GrowthAlertAudioPlayer
              url={lesson.thinkingOpenerAudioUrl}
              label={t('growthAlert.thinkingOpener')}
            />
          ) : null}
        </StepCard>
      ) : null}

      {explainStep ? (
        <StepCard
          step={explainStep}
          title={t('growthAlert.explain')}
          description={t('growthAlert.explainDescription')}
          surface={surface}
        >
          <div className="space-y-6">
            {lesson.explain.photoUrls.length > 0 ? (
              <div>
                <Caption icon={<ImageIcon className="size-5 text-[#29b4b5]" aria-hidden="true" />}>
                  {t('growthAlert.studentThought')}
                </Caption>
                <div className="mt-2.5 flex flex-wrap gap-2">
                  {lesson.explain.photoUrls.map((url, photoIndex) => {
                    const photoLabel = t('growthAlert.photoAlt', { index: photoIndex + 1 })
                    return <PhotoThumbnail key={url} url={url} label={photoLabel} onOpenPhoto={onOpenPhoto} />
                  })}
                </div>
              </div>
            ) : null}

            <ExplainVideos videos={lesson.explain.videos ?? []} t={t} onRefreshVideoUrl={onRefreshVideoUrl} />

            {explainRecordings.map(({ key, url, label }) => url ? (
              <div key={key}>
                <Caption icon={<Mic2 className="size-[18px] text-[#4599ff]" aria-hidden="true" />}>
                  {label}
                </Caption>
                <div className="mt-2.5">
                  <GrowthAlertAudioPlayer url={url} label={label} />
                </div>
              </div>
            ) : null)}
          </div>
        </StepCard>
      ) : null}

      {outcomesStep ? (
        <StepCard
          step={outcomesStep}
          title={t('growthAlert.learningResults')}
          surface={surface}
        >
          <div className="space-y-2 md:space-y-4">
            {lesson.lessonQuizSummary ? (
              <OutcomeCard
                icon={<Pencil className="size-[17px]" aria-hidden="true" />}
                label={t('growthAlert.lessonQuiz')}
                correctCount={lesson.lessonQuizSummary.correctCount}
                totalCount={lesson.lessonQuizSummary.totalCount}
                tone="blue"
                t={t}
              />
            ) : null}
            {reviewNoteSummaries.map((summary, index) => (
              <ReviewNoteOutcome
                key={`${summary.status}-${summary.sourceMode ?? index}`}
                summary={summary}
                t={t}
              />
            ))}
            {lesson.workbookSummary ? (
              <WorkbookOutcome summary={lesson.workbookSummary} t={t} />
            ) : null}
          </div>
        </StepCard>
      ) : null}

      {checksStep ? (
        <StepCard
          step={checksStep}
          title={t('growthAlert.essentialChecks')}
          surface={surface}
        >
          <EssentialChecks checks={lesson.essentialChecks} t={t} />
        </StepCard>
      ) : null}

      <ComponentPhotoGallery groups={lesson.componentPhotos} step={nextStep} surface={surface} t={t} onOpenPhoto={onOpenPhoto} />
    </>
  )
}

function PhotoThumbnail({ url, label, onOpenPhoto, onError }: {
  url: string
  label: string
  onOpenPhoto: (url: string) => void
  onError?: () => void
}) {
  return <button
    type="button"
    aria-label={label}
    onClick={() => onOpenPhoto(url)}
    className="overflow-hidden rounded-xl border border-black bg-[#f5f5f5] transition-opacity hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#4599ff]"
  >
    <img src={url} alt={label} className="h-[88px] w-[140px] object-cover" onError={onError} />
  </button>
}

function ComponentPhotoGallery({ groups, step, surface = 'dialog', t, onOpenPhoto }: {
  groups: GrowthAlertComponentPhotosView[] | undefined
  step?: number
  surface?: GrowthAlertSurface
  t: TFunc
  onOpenPhoto: (url: string) => void
}) {
  const galleryId = useId()
  const [failedUrls, setFailedUrls] = useState<Set<string>>(() => new Set())
  const visibleGroups = (groups ?? []).map((group, index) => {
    const nameKey = COMPONENT_PHOTO_NAME_KEYS.get(group.componentType)
    const configuredLabel = group.componentLabel?.trim()
    const aliasKey = configuredLabel ? COMPONENT_PHOTO_LABEL_ALIASES.get(group.componentType)?.get(configuredLabel) : undefined
    const label = aliasKey
      ? t(aliasKey)
      : configuredLabel || (nameKey
        ? t(`growthAlert.photoComponentNames.${nameKey}`)
        : t('growthAlert.componentPhotoFallback', { index: index + 1 }))
    const photoUrls = [...new Set(group.photoUrls.map(url => url.trim()))].filter(url => url && !failedUrls.has(url))
    return { index, label, photoUrls }
  }).filter(group => group.photoUrls.length > 0)
  if (visibleGroups.length === 0) return null

  const content = <div className="flex flex-col gap-6">
    {visibleGroups.map(group => <section key={group.index} aria-labelledby={`${galleryId}-${group.index}`}>
      <h4 id={`${galleryId}-${group.index}`} className="flex items-center gap-1.5 text-sm font-medium text-[#333]">
        <ImageIcon className="size-5 text-[#29b4b5]" aria-hidden="true" /><ContentMetadataText text={group.label} />
      </h4>
      <div className="mt-2.5 flex flex-wrap gap-2">
        {group.photoUrls.map((url, index) => <PhotoThumbnail
          key={url}
          url={url}
          label={t('growthAlert.componentPhotoAlt', { component: group.label, index: index + 1 })}
          onOpenPhoto={onOpenPhoto}
          onError={() => setFailedUrls(current => new Set(current).add(url))}
        />)}
      </div>
    </section>)}
  </div>

  return step != null
    ? <StepCard step={step} title={t('growthAlert.componentPhotos')} surface={surface}>{content}</StepCard>
    : <LegacyField label={t('growthAlert.componentPhotos')}>{content}</LegacyField>
}

function StepCard({
  step,
  title,
  description,
  surface,
  children,
}: {
  step: number
  title: string
  description?: string
  surface: GrowthAlertSurface
  children: React.ReactNode
}) {
  return (
    <section
      className={clsx(
        'flex flex-col gap-6 rounded-2xl bg-white p-4 shadow-[0_6px_8px_rgba(0,0,0,0.05)] md:p-6',
        surface === 'public' && 'lg:flex-row lg:items-start',
      )}
    >
      <div className={clsx('min-w-0', surface === 'public' && 'lg:w-80 lg:shrink-0')}>
        <div
          className={clsx(
            'flex items-center gap-2.5',
            surface === 'public' && 'lg:flex-col lg:items-start lg:gap-2',
          )}
        >
          <span className="flex size-6 shrink-0 items-center justify-center rounded-lg bg-[#4599ff] text-sm font-bold text-white">
            {step}
          </span>
          <h3 className="text-lg font-bold md:text-xl">{title}</h3>
        </div>
        {description ? (
          <p className="mt-3 text-base font-medium leading-[1.5] md:leading-[1.6]">{description}</p>
        ) : null}
      </div>
      <div className="min-w-0 flex-1">{children}</div>
    </section>
  )
}

function Caption({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <p className="flex items-center gap-1.5 text-sm font-medium text-[#333]">
      {icon}
      {children}
    </p>
  )
}

function OutcomeCard({
  icon,
  label,
  headline,
  description,
  correctCount,
  totalCount,
  tone,
  t,
}: {
  icon: React.ReactNode
  label: string
  headline?: string
  description?: string
  correctCount?: number
  totalCount?: number | null
  tone: 'blue' | 'red' | 'mint'
  t: TFunc
}) {
  return (
    <div
      className={clsx(
        'rounded-2xl px-[18px] py-4',
        tone === 'blue' && 'bg-[#edf5ff]',
        tone === 'red' && 'bg-[#fff5f5]',
        tone === 'mint' && 'bg-[#e6f7f7]',
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <span className="flex min-w-0 max-w-full shrink-0 items-center gap-4 text-sm font-medium">
          <span
            className={clsx(
              'flex size-5 shrink-0 items-center justify-center',
              tone === 'blue' && 'text-[#4599ff]',
              tone === 'red' && 'text-[#f55d54]',
              tone === 'mint' && 'text-[#29b4b5]',
            )}
          >
            {icon}
          </span>
          <span className="min-w-0 break-words">{label}</span>
        </span>
        {headline ? (
          <span className="ml-auto min-w-0 max-w-full break-words text-right text-base font-semibold">{headline}</span>
        ) : correctCount != null && totalCount != null ? (
          <span className="ml-auto shrink-0">
            <span className="sr-only">
              {t('growthAlert.countValue', { correctCount, totalCount })}
            </span>
            <span className="flex items-baseline gap-1" aria-hidden="true">
              <strong className="text-base font-semibold">{correctCount}</strong>
              <span className="text-sm font-medium text-[#888]">
                {t('growthAlert.totalCountValue', { totalCount })}
              </span>
            </span>
          </span>
        ) : null}
      </div>
      {description ? (
        <p className={clsx('mt-3 text-xs font-medium', tone === 'red' ? 'text-[#f55d54]' : 'text-[#555]')}>
          {description}
        </p>
      ) : null}
    </div>
  )
}

function ReviewNoteOutcome({ summary, t }: { summary: GrowthAlertReviewNoteSummary; t: TFunc }) {
  if (summary.status === 'HAS_REVIEW_NOTE') {
    return (
      <OutcomeCard
        icon={<RefreshCw className="size-[17px]" aria-hidden="true" />}
        label={reviewNoteLabel(summary.sourceMode, t)}
        correctCount={summary.correctCount}
        totalCount={summary.totalCount}
        description={t('growthAlert.reviewPrompt')}
        tone="red"
        t={t}
      />
    )
  }

  if (summary.status === 'NO_WRONG_ANSWER') {
    return (
      <OutcomeCard
        icon={<RefreshCw className="size-[17px]" aria-hidden="true" />}
        label={t('growthAlert.reviewNote')}
        headline={t('growthAlert.reviewNone')}
        description={t('growthAlert.reviewNoneDescription')}
        tone="red"
        t={t}
      />
    )
  }

  return (
    <OutcomeCard
      icon={<RefreshCw className="size-[17px]" aria-hidden="true" />}
      label={t('growthAlert.reviewNote')}
      headline={t('growthAlert.reviewNotStarted')}
      tone="red"
      t={t}
    />
  )
}

function WorkbookOutcome({
  summary,
  t,
}: {
  summary: NonNullable<GrowthAlertLessonView['workbookSummary']>
  t: TFunc
}) {
  if (summary.status === 'COMPLETED') {
    return (
      <OutcomeCard
        icon={<BookOpen className="size-[17px]" aria-hidden="true" />}
        label={t('growthAlert.workbook')}
        correctCount={summary.earnedScore ?? summary.correctCount ?? 0}
        totalCount={summary.totalCount}
        tone="mint"
        t={t}
      />
    )
  }

  const isNextClass = summary.status === 'NEXT_CLASS'
  return (
    <OutcomeCard
      icon={<BookOpen className="size-[17px]" aria-hidden="true" />}
      label={t('growthAlert.workbook')}
      headline={isNextClass
        ? t('growthAlert.workbookNextClass')
        : t('growthAlert.workbookAssignment')}
      description={isNextClass ? undefined : t('growthAlert.workbookAssignmentDescription')}
      tone="mint"
      t={t}
    />
  )
}

function EssentialChecks({ checks, t }: { checks: GrowthAlertEssentialCheckView[]; t: TFunc }) {
  return (
    <ul className="space-y-2">
      {checks.map((check) => (
        <li
          key={check.checkNo}
          className="rounded-2xl bg-[#f5f5f5] px-[18px] py-4 text-sm font-medium"
        >
          <div className="flex flex-col gap-4 md:flex-row md:items-start">
            <span className="min-w-0 flex-1 whitespace-pre-wrap">
              <LearningContentText text={check.text ?? '-'} />
            </span>
            <UnderstandingLevel score={check.score} t={t} />
          </div>
        </li>
      ))}
    </ul>
  )
}

function UnderstandingLevel({ score, t }: { score: number | null; t: TFunc }) {
  if (score == null) {
    return <span className="shrink-0 text-xs text-[#888]">{t('growthAlert.scoreEmpty')}</span>
  }

  const normalizedScore = Math.min(5, Math.max(1, score))
  const tone = normalizedScore <= 2 ? '#f55d54' : normalizedScore === 3 ? '#ffb800' : '#2ac47e'

  return (
    <span className="flex shrink-0 items-center justify-between gap-3 md:min-w-[180px]">
      <span className="text-xs text-[#888]">{t('growthAlert.understandingLabel')}</span>
      <span className="flex items-center gap-2">
        <span className="min-w-12 text-right text-xs" style={{ color: tone }}>
          {t(`growthAlert.understandingLevel.${normalizedScore}`)}
        </span>
        <span className="flex gap-1" aria-label={t('growthAlert.scoreValue', { score: normalizedScore })}>
          {[1, 2, 3, 4, 5].map((dot) => (
            <span
              key={dot}
              className="size-2.5 rounded-full"
              style={{ backgroundColor: dot <= normalizedScore ? tone : '#d9d9d9' }}
            />
          ))}
        </span>
      </span>
    </span>
  )
}

function LegacyLessonContent({
  lesson,
  t,
  onOpenPhoto,
  onRefreshVideoUrl,
}: {
  lesson: GrowthAlertLessonView
  t: TFunc
  onOpenPhoto: (url: string) => void
  onRefreshVideoUrl?: (id: string) => Promise<string | undefined>
}) {
  const explainRecordings = [
    { key: 'first', url: lesson.explain.firstAudioUrl?.trim(), label: t('growthAlert.firstRecording') },
    { key: 'second', url: lesson.explain.audioUrl?.trim(), label: t('growthAlert.secondRecording') },
  ]

  return (
    <div className="space-y-4 rounded-2xl bg-white p-4 shadow-[0_6px_8px_rgba(0,0,0,0.05)] md:p-6">
      <LegacyField label={t('growthAlert.understanding')}>
        {lesson.understandingScore != null ? (
          <p className="text-sm font-semibold">
            {t('growthAlert.scoreValue', { score: lesson.understandingScore })}
          </p>
        ) : (
          <p className="text-sm text-[#888]">{t('growthAlert.scoreEmpty')}</p>
        )}
      </LegacyField>

      <div className="grid gap-4 sm:grid-cols-2">
        <LegacyField label={t('growthAlert.strengths')}>
          <SignalSentences signals={lesson.strengthSignals} kind="strength" t={t} />
        </LegacyField>
        <LegacyField label={t('growthAlert.weaknesses')}>
          <SignalSentences signals={lesson.weaknessSignals} kind="weakness" t={t} />
        </LegacyField>
      </div>

      {lesson.essentialChecks.length > 0 ? (
        <LegacyField label={t('growthAlert.essentialChecks')}>
          <EssentialChecks checks={lesson.essentialChecks} t={t} />
        </LegacyField>
      ) : null}

      {lesson.thinkingPoint ? (
        <LegacyField label={t('growthAlert.thinkingOpener')}>
          <p className="whitespace-pre-wrap text-sm font-medium">
            <LearningContentText text={lesson.thinkingPoint} />
          </p>
        </LegacyField>
      ) : null}

      {lesson.thinkingOpenerAudioUrl ? (
        <GrowthAlertAudioPlayer
          url={lesson.thinkingOpenerAudioUrl}
          label={t('growthAlert.thinkingOpener')}
        />
      ) : null}

      {lesson.explain.questionText ? (
        <LegacyField label={t('growthAlert.explain')}>
          <p className="whitespace-pre-wrap text-sm">
            <LearningContentText text={lesson.explain.questionText} />
          </p>
        </LegacyField>
      ) : null}

      {lesson.explain.photoUrls.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {lesson.explain.photoUrls.map((url, index) => (
            <button
              key={url}
              type="button"
              onClick={() => onOpenPhoto(url)}
              aria-label={t('growthAlert.photoAlt', { index: index + 1 })}
              className="overflow-hidden rounded-xl border border-gray-200"
            >
              <img
                src={url}
                alt={t('growthAlert.photoAlt', { index: index + 1 })}
                className="h-[88px] w-[140px] object-cover"
              />
            </button>
          ))}
        </div>
      ) : null}

      <ExplainVideos videos={lesson.explain.videos ?? []} t={t} onRefreshVideoUrl={onRefreshVideoUrl} />

      {explainRecordings.map(({ key, url, label }) => url ? (
        <LegacyField key={key} label={label}>
          <GrowthAlertAudioPlayer url={url} label={label} />
        </LegacyField>
      ) : null)}

      <ComponentPhotoGallery groups={lesson.componentPhotos} t={t} onOpenPhoto={onOpenPhoto} />
    </div>
  )
}

function ExplainVideos({ videos, t, onRefreshVideoUrl }: {
  videos: GrowthAlertVideoView[]
  t: TFunc
  onRefreshVideoUrl?: (id: string) => Promise<string | undefined>
}) {
  if (videos.length === 0) return null
  return (
    <div className="space-y-3" data-testid="growth-alert-videos">
      {videos.map((video, index) => (
        <div key={`${video.id}:${video.version}`}>
          {video.status === 'READY' && video.playbackUrl ? (
            <LcEvidenceVideoPlayer
              url={video.playbackUrl}
              label={t('growthAlert.videoLabel', { index: index + 1 })}
              onRefreshUrl={onRefreshVideoUrl ? () => onRefreshVideoUrl(video.id) : undefined}
            />
          ) : <p role="status" className={video.status === 'FAILED' ? 'text-sm text-destructive' : 'text-sm text-muted-foreground'}>
            {t(video.status === 'FAILED' ? 'growthAlert.videoFailed' : video.status === 'READY' ? 'growthAlert.videoUnavailable' : 'growthAlert.videoProcessing')}
          </p>}
        </div>
      ))}
    </div>
  )
}

function LegacyField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-1 text-xs font-semibold text-[#888]">{label}</p>
      {children}
    </div>
  )
}

function SignalSentences({
  signals,
  kind,
  t,
}: {
  signals: UnderstandingSignalKey[]
  kind: 'strength' | 'weakness'
  t: TFunc
}) {
  if (signals.length === 0) {
    return <p className="text-sm text-[#888]">{t('growthAlert.signalEmpty')}</p>
  }
  const prefix = kind === 'strength' ? 'growthAlert.strengthSignal' : 'growthAlert.weaknessSignal'
  return (
    <ul className="space-y-1">
      {signals.map((signal) => (
        <li key={signal} className="text-sm">
          {t(`${prefix}.${signal}`)}
        </li>
      ))}
    </ul>
  )
}

function reviewNoteLabel(
  sourceMode: GrowthAlertReviewNoteSummary['sourceMode'],
  t: TFunc,
): string {
  if (sourceMode === 'ALL') return t('growthAlert.reviewNoteAll')
  if (sourceMode === 'ESSENTIAL') return t('growthAlert.reviewNoteEssential')
  return t('growthAlert.reviewNote')
}

function orderedReviewNoteSummaries(lesson: GrowthAlertLessonView): GrowthAlertReviewNoteSummary[] {
  const summaries = lesson.reviewNoteSummaries?.length
    ? lesson.reviewNoteSummaries
    : lesson.reviewNoteSummary
      ? [lesson.reviewNoteSummary]
      : []
  return [...summaries].sort((left, right) => (
    (REVIEW_NOTE_SOURCE_ORDER[left.sourceMode ?? ''] ?? 3) -
      (REVIEW_NOTE_SOURCE_ORDER[right.sourceMode ?? ''] ?? 3)
  ))
}

function dedupeSubtitleParts(parts: Array<string | null>): string[] {
  const seen = new Set<string>()
  return parts.flatMap((part) => {
    const value = part?.trim()
    if (!value || seen.has(value)) return []
    seen.add(value)
    return [value]
  })
}

function formatSessionLine(session: GrowthAlertSessionView): string {
  const [year, month, day] = session.date.split('-')
  const datePart = `${year}.${month}.${day}`
  const locale = getDateLocale()
  const dateObj = new Date(Number(year), Number(month) - 1, Number(day), 12)
  let line = `${datePart} (${format(dateObj, 'EEE', { locale })})`

  if (session.displayStartedAt) {
    const startedAt = new Date(session.displayStartedAt)
    const endedAt = session.displayEndedAt
      ? new Date(session.displayEndedAt)
      : session.durationMinutes == null
        ? null
        : new Date(startedAt.getTime() + session.durationMinutes * 60_000)
    line += ` ${format(startedAt, 'HH:mm', { locale })}`
    if (endedAt) line += ` ~ ${format(endedAt, 'HH:mm', { locale })}`
  }

  return line
}

function ReportStatus({ tone, message }: { tone: 'muted' | 'error'; message: string }) {
  return (
    <div className="flex min-h-72 items-center justify-center bg-[#f7f7f7] p-8">
      <p
        role={tone === 'error' ? 'alert' : 'status'}
        className={clsx('text-sm', tone === 'error' ? 'text-red-600' : 'text-[#888]')}
      >
        {message}
      </p>
    </div>
  )
}
